import type { Field, Project } from "@proof/core";

/** Plain-language names for field types, grouped the way people look for them. */
export const fieldTypeInfo: Record<
  Field["type"],
  { name: string; description: string; group: string; icon: string }
> = {
  text: {
    name: "Short text",
    description: "A single line, like a name or title.",
    group: "Text",
    icon: "T",
  },
  textarea: {
    name: "Paragraph",
    description: "Several lines of plain text.",
    group: "Text",
    icon: "¶",
  },
  richtext: {
    name: "Formatted text",
    description: "Text with bold, italics and lists.",
    group: "Text",
    icon: "B",
  },
  number: {
    name: "Number",
    description: "A quantity, price or other number.",
    group: "Text",
    icon: "#",
  },
  email: {
    name: "Email address",
    description: "Checked to look like an email.",
    group: "Contact",
    icon: "@",
  },
  phone: {
    name: "Phone number",
    description: "Formatted as a phone number.",
    group: "Contact",
    icon: "☏",
  },
  zip: {
    name: "ZIP code",
    description: "US ZIP or ZIP+4; keeps leading zeros.",
    group: "Contact",
    icon: "⌂",
  },
  uri: {
    name: "URI / website",
    description: "A website or other complete URI.",
    group: "Contact",
    icon: "↗",
  },
  address: {
    name: "Address",
    description: "A postal address.",
    group: "Contact",
    icon: "⌂",
  },
  map: {
    name: "Map",
    description: "A location shown on a map.",
    group: "Contact",
    icon: "⌖",
  },
  dropdown: {
    name: "Dropdown list",
    description: "Pick one option from a list.",
    group: "Choices",
    icon: "☷",
  },
  combobox: {
    name: "Searchable list",
    description: "Type to search, or enter a new value.",
    group: "Choices",
    icon: "⌕",
  },
  radio: {
    name: "Multiple choice",
    description: "Pick one option, all shown at once.",
    group: "Choices",
    icon: "◉",
  },
  checkbox: {
    name: "Checkbox",
    description: "Choose one or more options from a list.",
    group: "Choices",
    icon: "☑",
  },
  preset: {
    name: "Preset",
    description: "One choice that fills in several fields.",
    group: "Choices",
    icon: "★",
  },
  color: {
    name: "Color",
    description: "Pick a color.",
    group: "Choices",
    icon: "◐",
  },
  date: {
    name: "Date",
    description: "A calendar date.",
    group: "Date & time",
    icon: "▤",
  },
  datetime: {
    name: "Date and time",
    description: "A date with a time of day.",
    group: "Date & time",
    icon: "◷",
  },
  time: {
    name: "Time",
    description: "A time of day.",
    group: "Date & time",
    icon: "◔",
  },
  calendar: {
    name: "Calendar",
    description: "A month calendar with events.",
    group: "Date & time",
    icon: "▦",
  },
  image: {
    name: "Image",
    description: "Upload or choose one picture.",
    group: "Media",
    icon: "▧",
  },
  "image-list": {
    name: "Image gallery",
    description: "Choose from a set of pictures.",
    group: "Media",
    icon: "▨",
  },
  qr: {
    name: "QR code",
    description: "Turns a link or text into a QR code.",
    group: "Media",
    icon: "▩",
  },
};
export const fieldTypeName = (type: Field["type"]) =>
  fieldTypeInfo[type]?.name || type;

/** A readable, unique variable token derived from a field label. */
export function fieldKeyFromLabel(label: string, project: Project): string {
  const words = label
    .normalize("NFKD")
    .replace(/[^\w\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const base =
    words
      .map((word, i) =>
        i === 0
          ? word.toLowerCase()
          : word[0].toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join("")
      .replace(/^(\d)/, "field$1") || "field";
  const taken = new Set(project.fields.map((f) => f.key));
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}${n}`;
  return key;
}
